<script setup lang="ts" generic="T extends string">
/**
 * A select you can type into: the field narrows its option list as you type,
 * but the value still comes from that list — free text is never taken.
 *
 * Structurally this is an autocomplete, not a select: the trigger is a real
 * `NxTextInput`, so there is no `NxFakeInput` div pretending to be a field. What
 * it is *not* is `NxAutocompleteInput`, which resolves a foreign key against a
 * remotely loaded `Map<number, string>` with two models. This one holds the
 * complete option list locally and keeps `NxSelectInput`'s single model, so the
 * two share their value logic (`useSelectSelection`) and their dropdown body
 * (`SelectOptionList`) rather than their trigger.
 *
 * Because the trigger is an input, it can only show plain text. Consumers whose
 * options render as markup (a status icon per option) belong on `NxSelectInput`.
 */
import { ChevronUpDownIcon } from '@blpde/nx-icons';
import { computed, inject, nextTick, onUnmounted, ref, type StyleValue, useId, watch } from 'vue';
import { NxDropdown } from '../Dropdown';
import { i18nInjectionKey } from '../internal';
import { useSelectDismiss } from './composables/selectDismiss';
import { filterOptionIndices, type SelectFilterMode } from './composables/selectFilter';
import { placeholderRowReachable, selectCloseAction } from './composables/selectPolicy';
import { useSelectSelection } from './composables/selectSelection';
import { FONT_SIZE_LOOKUP, InputHeight, loadingStateInjectionKey } from './internal';
import NxTextInput from './NxTextInput.vue';
import SelectOptionList from './SelectOptionList.vue';

const props = defineProps<{
  id?: string;
  /** The options to display */
  options: T[];
  placeholder: string;
  wrapperStyle?: StyleValue;
  height?: InputHeight;
  multiple?: boolean;
  disabled?: boolean;
  /** User can re-select the placeholder. Defaults to false. */
  allowNull?: boolean;
  /**
   * When `true`, the chevron icon is hidden until the field is hovered or
   * focused, then fades in — its space stays reserved, so nothing shifts. Useful
   * in dense layouts (e.g. table cells) to cut visual noise. Opt-in; defaults to
   * `false`.
   */
  revealIcon?: boolean;
  /** Hide the chevron entirely when an adjacent control already exposes the picker. */
  hideIcon?: boolean;
  /**
   * Plain-text twin of an option. Feeds the filter and the tooltip. The `#option`
   * slot owns the markup in the dropdown; this owns the text, because a slot may
   * render a translated label (enum fields) — matching the raw value against
   * what the user reads finds nothing. Defaults to `String(option)`.
   */
  optionText?: (option: T) => string;
  /**
   * How a typed query matches. Defaults to `'contains'`, so a query is found
   * anywhere in the label; `'startsWith'` restricts it to a prefix.
   */
  filterMode?: SelectFilterMode;
  /** Keyboard shortcuts implemented by the surrounding field owner. */
  ariaKeyshortcuts?: string;
}>();

const elementId = props.id ?? useId();
const listboxId = `${elementId}-listbox`;

const [modelValue, modifier] = defineModel<T[] | [null], 'lazy'>({ required: true });

const {
  shadowValue,
  labelOf,
  optionTexts,
  triggerTitle,
  selectionText,
  toggleOption: applyOption,
  selectPlaceholder: applyPlaceholder,
  commitIfLazy,
  revert,
} = useSelectSelection(props, modelValue, modifier);

const loadingState = inject(loadingStateInjectionKey, null);
const disabled = computed(() => props.disabled || loadingState?.status.value?.disabled);

const i18nInjection = inject(i18nInjectionKey)!;

const isOpen = ref(false);
const highlightedIndex = ref(-1);
/** What stands in the field while it is open — the selection until the user edits it. */
const query = ref('');
/**
 * Whether `query` is the user's search rather than the selection the field was
 * opened with. Opening pre-fills the input with the current value (that is what
 * makes the value a real input value instead of a placeholder), and filtering on
 * that would leave exactly the already-chosen option in the list.
 */
const queryEdited = ref(false);

/** Filtering is on only once the user has actually typed something. */
const isFiltering = computed(() => queryEdited.value && query.value.trim().length > 0);

/**
 * Whether something was chosen since the field opened. An emptied field means
 * "clear the value" only if the user did not go on to pick something — otherwise
 * clearing the text and then clicking an option would commit `null` over the
 * pick on the way out.
 */
const pickedSinceOpen = ref(false);

const inputRef = ref<{
  inputElement?: HTMLInputElement | null;
  wrapperElement?: HTMLElement | null;
}>();
const dropdownRef = ref<{
  listElement?: HTMLElement | null;
  scrollHighlightIntoView?: () => void;
}>();

/**
 * The whole field, not just the input: the click-outside check has to treat the
 * wrapper's padding and its inline icons as "inside", or the first click on them
 * closes the list. Taken from the wrapper itself rather than searched back by
 * class name — a renamed class would fail silently and leave the field closing on
 * its own chevron. No wrapper div of our own: not having one is the point of this
 * component.
 */
function fieldElement(): Element | null {
  return inputRef.value?.wrapperElement ?? null;
}

function focusInput() {
  inputRef.value?.inputElement?.focus();
}

/**
 * Put the caret back in the field *and* select its text. Not just `focus()`: the
 * option rows suppress their mousedown default so the field never loses focus, and
 * `focus()` on an already-focused element fires no event — so the `focus` handler
 * below would not run, the text would stay unselected, and the next keystroke
 * would append to the value instead of replacing it.
 *
 * Only for the close paths that mean "the user is done choosing and stays here":
 * a pick, Enter, Escape. Everything else leaves the caret alone — `NxDropdown`
 * closes on outside scroll and a host can close the field from the outside, and
 * re-selecting the whole value mid-interaction is exactly as unwelcome there as
 * pulling the focus back from another field. Tab is not one of them either: it
 * means "leave".
 *
 * Asking whether the focus is still in the field cannot tell those apart — on an
 * outside scroll it is, which is why this is an explicit intent instead.
 */
let reclaimFocusOnClose = false;

function focusAndSelectInput() {
  const element = inputRef.value?.inputElement;
  if (!element) return;
  element.focus();
  // After the render, not before it: this runs from the `isOpen` watcher, and the
  // new value reaches the element only on the following patch — selecting first
  // would be undone by it, leaving the caret at the end and typing appending again.
  nextTick(() => element.select());
}

/**
 * The text in the field: the selection while closed, the editable query while
 * open. The selection is the input's **value**, not its placeholder — a
 * placeholder that sometimes means "nothing chosen" and sometimes "this is your
 * value" reads as two different things in one slot, and `NxAutocompleteInput`
 * has shown for years that the value belongs in the value.
 */
const inputModel = computed<string>({
  get: () => (isOpen.value ? query.value : selectionText.value),
  set: (value) => {
    query.value = value;
    queryEdited.value = true;
  },
});

/**
 * Which options survive the query, as positions in `props.options`. Kept as
 * indices rather than mapped away, because the list needs them as row identity:
 * a position is unique even when two options read the same, and it does not move
 * when the query narrows.
 */
const visibleIndices = computed<number[]>(() =>
  isFiltering.value
    ? filterOptionIndices(optionTexts.value, query.value, props.filterMode)
    : props.options.map((_, index) => index)
);

const visibleOptions = computed<T[]>(() => visibleIndices.value.map((i) => props.options[i]));

const activeOptionId = computed(() => {
  if (!isOpen.value) return undefined;
  if (highlightedIndex.value === -1) {
    return clearRowReachable.value ? `${listboxId}-placeholder` : undefined;
  }
  const sourceIndex = visibleIndices.value[highlightedIndex.value];
  return sourceIndex == null ? undefined : `${listboxId}-option-${sourceIndex}`;
});

/**
 * Select the text on entering the field so the first keystroke replaces the
 * current value instead of appending to it — without this, typing into a field
 * holding "Postanschrift" searches for "Postanschriftx".
 *
 * A bare `focus` handler is not enough: on a mouse click the browser fires focus
 * first and *then* places the caret as part of the same default action, throwing
 * the selection away. So a click defers the select to `mouseup`, and only a
 * keyboard focus selects straight away. A second click inside an already focused
 * field places the caret normally, as it should.
 */
let selectOnMouseUp = false;

function releaseMouseLatch() {
  selectOnMouseUp = false;
}

function onMouseDown() {
  if (inputRef.value?.inputElement === document.activeElement) return;
  selectOnMouseUp = true;
  // The release can land anywhere — drag out of the field and the input's own
  // mouseup never fires. Without this the latch would stay set for good and every
  // later keyboard focus would skip selecting, which is the very thing it exists
  // to guarantee. Bubble phase, so the input's own handler still goes first.
  document.addEventListener('mouseup', releaseMouseLatch, { once: true });
}

// `{ once: true }` only fires on a mouseup that actually arrives: press inside a
// field that unmounts on the pick and the listener outlives the component,
// holding its whole setup scope until some mouseup happens anywhere.
onUnmounted(() => document.removeEventListener('mouseup', releaseMouseLatch));

function onMouseUp() {
  if (!selectOnMouseUp) return;
  selectOnMouseUp = false;
  const element = inputRef.value?.inputElement;
  // A drag already selected something — that is the user aiming at a substring,
  // so leave it alone and only select all when the click left a bare caret.
  if (element && element.selectionStart === element.selectionEnd) element.select();
}

function onFocus() {
  if (selectOnMouseUp) return; // the click path selects on mouseup
  inputRef.value?.inputElement?.select();
}

function onQueryInput() {
  isOpen.value = true;
}

/**
 * Whether the clear row can be *aimed at* — the one answer the highlight, the
 * arrow keys and Enter all read, so they cannot disagree about what `-1` means.
 *
 * Not the same question as whether the row is in the DOM: `SelectOptionList`
 * renders it for `!multiple && showPlaceholder` and merely greys it out when
 * `allowNull` is false. Reachable is the narrower of the two, so a row that is
 * visible but refuses to be picked can never hold the highlight.
 */
const clearRowReachable = computed(() =>
  placeholderRowReachable({
    allowNull: props.allowNull,
    multiple: props.multiple,
    filtering: isFiltering.value,
  })
);

/**
 * Where the highlight belongs right now: the first surviving option, so Enter
 * takes the obvious hit — the clear row (-1) only while it is actually offered,
 * and nothing at all over an empty list. Both watchers below go through this;
 * computing it in two places is how "highlight row 0" ended up pointing at a
 * zero-row list.
 */
/** Set when opening should land on a row rather than on the default. */
let requestedHighlight: number | null = null;

function resetHighlight() {
  const requested = requestedHighlight;
  requestedHighlight = null;

  if (visibleOptions.value.length === 0) {
    highlightedIndex.value = -1;
    return;
  }
  if (requested !== null) {
    highlightedIndex.value = Math.min(requested, visibleOptions.value.length - 1);
    return;
  }
  highlightedIndex.value = clearRowReachable.value ? -1 : 0;
}

// Pre-filling on open is not an edit and leaves the highlight alone.
watch(query, () => {
  if (!queryEdited.value) return;
  resetHighlight();
});

function openField() {
  if (disabled.value) return;
  // Only ever opens, never toggles: the user clicks to place the caret, and a
  // toggle would close the list mid-typing.
  isOpen.value = true;
  focusInput();
}

function focus() {
  focusInput();
}

function open() {
  openField();
}

defineExpose({ focus, open });

function toggleOption(option: T) {
  pickedSinceOpen.value = true;
  if (applyOption(option)) {
    reclaimFocusOnClose = true;
    isOpen.value = false;
  }
}

function selectPlaceholder() {
  pickedSinceOpen.value = true;
  reclaimFocusOnClose = true;
  applyPlaceholder();
  isOpen.value = false;
}

// Escape abandons the whole session: the close branch below drops the query and
// reverts anything a lazy model had not committed.
const { cancelled } = useSelectDismiss(isOpen, {
  fieldElement,
  listElement: () => dropdownRef.value?.listElement,
});

watch(isOpen, (open) => {
  if (open) {
    pickedSinceOpen.value = false;
    // Carry the current value into the editable text, unless the user opened the
    // field *by* typing — that keystroke is already the query.
    if (!queryEdited.value) query.value = selectionText.value;
    resetHighlight();
    // The list's own watcher fires on a *change* of the index. Reopening at the
    // index it was left on would show the list wherever an inside scroll had
    // pushed it, with the highlighted row off-screen and Enter aimed at it.
    nextTick(() => dropdownRef.value?.scrollHighlightIntoView?.());
  } else {
    // One decision, made in `selectPolicy` and tested there as a truth table:
    // spelling the conditions out here is how a fix for one close path kept
    // leaving the path beside it behind.
    const action = selectCloseAction({
      cancelled: cancelled.value,
      queryEdited: queryEdited.value,
      query: query.value,
      picked: pickedSinceOpen.value,
      allowNull: props.allowNull,
      multiple: props.multiple,
    });
    if (action === 'revert') {
      // Escape: drop whatever a lazy model has not committed yet, so cancelling
      // cannot commit the very picks it is backing out of.
      revert();
    } else {
      if (action === 'clear') applyPlaceholder();
      commitIfLazy();
    }
    // The query is a search, never a value: closing throws it away and the field
    // goes back to showing the selection.
    query.value = '';
    queryEdited.value = false;
    if (cancelled.value || reclaimFocusOnClose) focusAndSelectInput();
    reclaimFocusOnClose = false;
  }
});

function onKeydown(e: KeyboardEvent) {
  if (!isOpen.value) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.preventDefault();
      // Through the intent, not by assigning afterwards: the `isOpen` watcher runs
      // before the next render and would overwrite a direct assignment, which is
      // why opening with ArrowDown used to need a second press to leave the clear row.
      // ArrowUp opens at the bottom, like a native select and the ARIA combobox
      // pattern — landing on the first option would make a second press a dead key.
      requestedHighlight = e.key === 'ArrowUp' ? visibleOptions.value.length - 1 : 0;
      isOpen.value = true;
    }
    return;
  }

  const maxIndex = visibleOptions.value.length - 1;
  const minIndex = clearRowReachable.value ? -1 : 0;

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, maxIndex);
      break;
    case 'ArrowUp':
      e.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, minIndex);
      break;
    case 'Enter':
      e.preventDefault();
      if (highlightedIndex.value === -1 && clearRowReachable.value) selectPlaceholder();
      else if (highlightedIndex.value >= 0 && highlightedIndex.value <= maxIndex) {
        toggleOption(visibleOptions.value[highlightedIndex.value]);
      }
      break;
    case 'Tab':
      isOpen.value = false;
      break;
  }
  // Space is deliberately absent: while typing it is a literal character.
}
</script>

<template>
  <!-- fit-width, exactly like NxSelectInput: the option list sizes to its labels
       (≥ trigger width), so a long label in a narrow column is readable instead of
       clipped behind a horizontal scrollbar. The two fields differ in their
       trigger, not in their list. -->
  <NxDropdown :is-open="isOpen" fit-width @close="isOpen = false">
    <template #trigger>
      <NxTextInput
        :id="elementId"
        ref="inputRef"
        v-model="inputModel"
        :escape-restores="false"
        :wrapper-style="props.wrapperStyle"
        :height="props.height"
        :icons="
          props.hideIcon
            ? []
            : [
                {
                  align: 'end',
                  component: ChevronUpDownIcon,
                  filled: true,
                  // No handler, exactly like NxSelectInput's chevron: NxInputWrapper
                  // renders a clickable icon as a <button> and derives its disabled
                  // state from the wrapper's own attrs — which never carry `disabled`
                  // for a real input, since NxInput spreads $attrs onto the element.
                  // A handler here would therefore ship an enabled, focusable button on
                  // a disabled field, and cost every field a second tab stop.
                  // `deco: false` keeps it an affordance without making it an action.
                  deco: false,
                  revealOnInteraction: props.revealIcon,
                },
              ]
        "
        autocomplete="off"
        :placeholder="props.placeholder"
        :title="triggerTitle"
        :disabled="disabled"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOptionId"
        :aria-keyshortcuts="props.ariaKeyshortcuts"
        @click="openField"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
        @focus="onFocus"
        @keydown="onKeydown"
        @input="onQueryInput"
      />
    </template>
    <SelectOptionList
      :id="listboxId"
      ref="dropdownRef"
      v-model:highlighted-index="highlightedIndex"
      :options="visibleOptions"
      :keys="visibleIndices"
      :selected="shadowValue"
      :option-text="labelOf"
      :multiple="props.multiple"
      :allow-null="props.allowNull"
      :show-placeholder="!isFiltering"
      :placeholder="props.placeholder"
      :font-size="FONT_SIZE_LOOKUP[props.height ?? 's']"
      :empty-text="isFiltering ? i18nInjection.t('common.select.no_results') : undefined"
      @select="toggleOption"
      @select-placeholder="selectPlaceholder"
    >
      <template #placeholder>
        <slot name="placeholder" :placeholder="props.placeholder">
          {{ props.placeholder }}
        </slot>
      </template>
      <template #option="{ option }">
        <slot name="option" :option="option">
          {{ labelOf(option) }}
        </slot>
      </template>
    </SelectOptionList>
  </NxDropdown>
</template>
